import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { otpService, OtpType } from '../services/otpService';

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  role: 'brand' | 'creator' | 'agency' | 'admin';
  company_name?: string;
  avatar_url?: string;
}

interface OtpResult {
  success: boolean;
  error?: string;
  message?: string;
  user?: User;
  firebaseUser?: any;
  verificationId?: string;
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any; profile?: UserProfile | null }>;
  signUp: (email: string, password: string, userData: any) => Promise<{ error: any; profile?: UserProfile | null; message?: string }>;
  signInWithProvider: (provider: 'google' | 'apple' | 'github' | 'twitter') => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  // OTP methods
  sendOtp: (identifier: string, type: OtpType) => Promise<OtpResult>;
  verifyOtp: (identifier: string, token: string, type: OtpType) => Promise<OtpResult>;
  resendOtp: (identifier: string, type: OtpType) => Promise<OtpResult>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session - Use Promise chain
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        if (session?.user) {
          setUser(session.user);
          fetchUserProfile(session.user.id);
        }
        setLoading(false);
      });

    // Listen for auth changes - NEVER ASYNC callback
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: string, session: Session | null) => {
        if (session?.user) {
          setUser(session.user);
          fetchUserProfile(session.user.id);  // Fire-and-forget, NO AWAIT
        } else {
          setUser(null);
          setUserProfile(null);
        }
        setLoading(false);
      }
    );

    return () => subscription?.unsubscribe();
  }, []);

  const fetchUserProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      // Small delay to ensure auth session is fully established
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle(); // Use maybeSingle instead of single to avoid 406 if row doesn't exist
      
      if (error) {
        console.error('Error fetching user profile:', error);
        return null;
      }
      
      if (!data) {
        console.log('No profile found for user:', userId);
        return null;
      }
      
      setUserProfile(data);

      // For brand users, also fetch and set their brand ID
      if (data?.role === 'brand') {
        try {
          const { data: brandUserData } = await supabase
            .from('brand_users')
            .select('brand_id')
            .eq('user_id', userId)
            .limit(1)
            .maybeSingle();
          
          if (brandUserData?.brand_id) {
            localStorage.setItem('currentBrandId', brandUserData.brand_id);
          } else {
            // Fallback: use user ID as brand ID
            localStorage.setItem('currentBrandId', userId);
          }
        } catch {
          // If no brand_users entry, fallback to user ID
          localStorage.setItem('currentBrandId', userId);
        }
      }

      return data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setUserProfile(null);
      return null;
    }
  };

  const createProfileFallback = async (userId: string, email: string, userData: any): Promise<UserProfile | null> => {
    try {
      console.log('🔧 Trigger failed - Creating profile manually...');
      
      // Small delay to ensure auth session is fully established
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Normalize email
      const normalizedEmail = email.toLowerCase().trim();
      
      // First check if profile exists by ID
      let existingProfile = null;
      const { data: profileById, error: idError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (!idError && profileById) {
        existingProfile = profileById;
      } else {
        // Check by email if not found by ID
        const { data: profileByEmail, error: emailError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('email', normalizedEmail)
          .maybeSingle();
        
        if (!emailError && profileByEmail) {
          existingProfile = profileByEmail;
        }
      }

      let profile;
      
      // Validate role - ensure it's a valid enum value
      const validRoles = ['brand', 'creator', 'agency', 'admin'];
      const userRole = validRoles.includes(userData.role) ? userData.role : 'creator';
      
      if (existingProfile) {
        console.log('✓ Found existing profile, updating...');
        // Profile exists - update it
        const { data: updatedProfile, error: updateError } = await supabase
          .from('user_profiles')
          .update({
            full_name: userData.full_name || existingProfile.full_name || email.split('@')[0],
            role: userRole,
            company_name: userData.company_name || existingProfile.company_name,
            avatar_url: userData.avatar_url || existingProfile.avatar_url,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingProfile.id)
          .select()
          .maybeSingle();

        if (updateError) {
          console.error('Failed to update existing profile:', updateError);
          // If update failed, just return the existing profile
          profile = existingProfile;
        } else {
          profile = updatedProfile || existingProfile;
        }
      } else {
        console.log('✓ No existing profile, creating new...');
        // No existing profile - create new one
        const { data: newProfile, error: insertError } = await supabase
          .from('user_profiles')
          .insert({
            id: userId,
            email: normalizedEmail,
            full_name: userData.full_name || email.split('@')[0],
            role: userRole,
            company_name: userData.company_name || null,
            avatar_url: userData.avatar_url || null,
            is_verified: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select()
          .maybeSingle();

        if (insertError) {
          console.error('Failed to insert new profile:', insertError);
          
          // Wait a bit and try to fetch (in case trigger created it)
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // If insert fails (race condition), try to fetch existing
          const { data: fetchedProfile } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('id', userId)
            .maybeSingle();
          
          if (fetchedProfile) {
            profile = fetchedProfile;
          } else {
            console.error('❌ Could not create or fetch profile');
            return null;
          }
        } else {
          profile = newProfile;
        }
      }

      if (!profile) {
        console.error('❌ No profile created or found');
        return null;
      }

      // If creator, create creator_profile too (use upsert to avoid conflicts)
      if (profile.role === 'creator') {
        console.log('✓ Creating/updating creator profile...');
        const { error: creatorError } = await supabase
          .from('creator_profiles')
          .upsert({
            user_id: userId,
            username: userData.username || email.split('@')[0],
            category: userData.category || 'General'
          }, { 
            onConflict: 'user_id',
            ignoreDuplicates: true 
          });

        if (creatorError) {
          console.error('Failed to create creator profile:', creatorError);
        }
      }

      console.log('✅ Profile created/updated successfully via fallback');
      setUserProfile(profile);
      return profile;
    } catch (error) {
      console.error('❌ Failed to create profile fallback:', error);
      return null;
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        console.error('❌ Sign in error:', error);
        
        // Provide more specific error messages
        let errorMessage = error.message;
        
        if (error.message?.includes('Email not confirmed')) {
          errorMessage = 'Please confirm your email before signing in. Check your inbox for a confirmation link.';
        } else if (error.message?.includes('Invalid login credentials')) {
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
        } else if (error.message?.includes('Email link is invalid or has expired')) {
          errorMessage = 'The verification link has expired. Please sign up again.';
        } else if (error.status === 400) {
          errorMessage = 'Unable to sign in. Please check your email and password, or try signing up if you don\'t have an account.';
        }
        
        return { error: { message: errorMessage } };
      }
      
      if (!data.user) return { error: { message: 'No user returned' } };

      // Wait for profile to load
      const profile = await fetchUserProfile(data.user.id);
      
      // If no profile, try to create it (fallback)
      if (!profile) {
        console.log('⚠️ No profile found - attempting fallback creation');
        const createdProfile = await createProfileFallback(
          data.user.id,
          data.user.email || email,
          { role: 'creator' } // Default to creator if unknown
        );
        
        if (!createdProfile) {
          return { 
            error: { message: 'Failed to create user profile. Please contact support.' } 
          };
        }
        
        return { error: null, profile: createdProfile };
      }

      return { error: null, profile: profile };
    } catch (error: any) {
      console.error('❌ Sign in exception:', error);
      return { error: { message: error.message || 'Sign in failed' } };
    }
  };

  const signUp = async (email: string, password: string, userData: any) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData,
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) {
        console.error('❌ Sign up error:', error);
        
        // Provide more specific error messages
        let errorMessage = error.message;
        
        if (error.message?.includes('already registered')) {
          errorMessage = 'This email is already registered. Please sign in instead.';
        } else if (error.message?.includes('Password should be at least')) {
          errorMessage = 'Password must be at least 6 characters long.';
        } else if (error.message?.includes('invalid email')) {
          errorMessage = 'Please enter a valid email address.';
        }
        
        return { error: { message: errorMessage } };
      }
      
      if (!data.user) return { error: { message: 'No user returned' } };

      console.log('✅ User created:', data.user.id);
      console.log('📧 Email confirmation required:', data.user.confirmation_sent_at ? 'Yes' : 'No');

      // Wait a moment for trigger to fire
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Check if profile was created by trigger
      let profile = await fetchUserProfile(data.user.id);

      // If trigger failed, create profile manually (fallback)
      if (!profile) {
        console.log('⚠️ Trigger failed - creating profile via fallback');
        profile = await createProfileFallback(
          data.user.id,
          data.user.email || email,
          userData
        );

        if (!profile) {
          return {
            error: { message: 'Account created but profile setup failed. Please contact support.' }
          };
        }
      }

      // Check if email confirmation is required
      if (data.session === null) {
        console.log('⚠️ Email confirmation required before sign-in');
        return { 
          error: null, 
          profile: profile,
          message: 'Account created! Please check your email to confirm your account before signing in.'
        };
      }

      return { error: null, profile: profile };
    } catch (error: any) {
      console.error('❌ Sign up exception:', error);
      return { error: { message: error.message || 'Sign up failed' } };
    }
  };

  const signOut = async (): Promise<void> => {
    await supabase.auth.signOut();
  };

  const signInWithProvider = async (provider: 'google' | 'apple' | 'github' | 'twitter') => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) return { error };
      return { error: null };
    } catch (error: any) {
      return { error: { message: error.message || 'Social sign in failed' } };
    }
  };

  // OTP Authentication Methods
  const sendOtp = async (identifier: string, type: OtpType): Promise<OtpResult> => {
    try {
      const result = type === 'email'
        ? await otpService.sendEmailOtp(identifier)
        : await otpService.sendPhoneOtp(identifier);

      return {
        success: result.success,
        error: result.error,
        message: result.message,
      };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to send OTP' };
    }
  };

  const verifyOtp = async (identifier: string, token: string, type: OtpType): Promise<OtpResult> => {
    try {
      const result = type === 'email'
        ? await otpService.verifyEmailOtp(identifier, token)
        : await otpService.verifyPhoneOtp(identifier, token);

      if (!result.success) {
        return { success: false, error: result.error };
      }

      // Handle email OTP verification
      if (type === 'email' && result.user) {
        console.log('✅ Email verified:', result.user.email);
        
        return {
          success: true,
          user: result.user,
          session: result.session,
          message: 'Email verified successfully',
        };
      }

      // Handle phone OTP verification
      if (type === 'phone' && result.user) {
        console.log('✅ Phone verified:', result.user.phone);
        
        return {
          success: true,
          user: result.user,
          session: result.session,
          message: 'Phone verified successfully',
        };
      }

      return {
        success: true,
        message: 'OTP verified successfully',
      };
    } catch (error: any) {
      return { success: false, error: error.message || 'OTP verification failed' };
    }
  };

  const resendOtp = async (identifier: string, type: OtpType): Promise<OtpResult> => {
    try {
      const result = await otpService.resendOtp(identifier, type);
      
      return {
        success: result.success,
        error: result.error,
        message: result.message,
      };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to resend OTP' };
    }
  };

  const value: AuthContextType = {
    user,
    userProfile,
    loading,
    signIn,
    signUp,
    signInWithProvider,
    signOut,
    // OTP methods
    sendOtp,
    verifyOtp,
    resendOtp,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};